import React, { useState, useEffect } from "react";
import {
  fetchOrderDetail,
  acceptOrder,
  declineOrder,
  deliverOrder,
} from "../../services/orders.service";
import { getUser } from "../../lib/authToken";
import { DEV_USER } from "../../config/localAuth";
import { http } from "../../lib/http";
import { LuX, LuCheck, LuTruck } from "react-icons/lu";

// Helper function to safely convert order ID to string
const getOrderIdString = (id) => {
  if (!id) return "";
  if (typeof id === "string" || typeof id === "number") return String(id);
  if (typeof id === "object" && id.id) return String(id.id);
  if (typeof id === "object") return JSON.stringify(id);
  return String(id);
};
// Normalize table number safely to string
const normalizeTableName = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "object" && val.table_number)
    return String(val.table_number);
  return String(val);
};

export default function OrderDetailsModal({
  order,
  isOpen,
  onClose,
  onStatusUpdate,
}) {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [tableName, setTableName] = useState("");
  // const [savingTable, setSavingTable] = useState(false);
  const [isEditingTable, setIsEditingTable] = useState(false);

  const user = getUser() || DEV_USER;
  const orgId = user.restaurant_id || 101;
  const location = user.location || "kphb";

  useEffect(() => {
    if (isOpen && order?.id) {
      loadOrderDetails();
      // Initialize table name from order data
      // setTableName(order?.table_number || order?.table_number || "");
      setTableName(orderDetails?.table_number || order?.table_number || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order?.id]);

  const loadOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the order ID as-is for fetching details (API expects full format)
      const orderIdForDetails = order.id;
      const details = await fetchOrderDetail(
        orderIdForDetails,
        orgId,
        location
      );
      setOrderDetails(details);
    } catch (err) {
      setError(err.message || "Failed to load order details");
      console.error("Error loading order details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (action) => {
    if (!order?.id) return;

    setUpdating(true);
    setError(null);
    try {
      let response;
      switch (action) {
        case "accept":
          response = await acceptOrder(order.id);
          break;
        case "decline":
          response = await declineOrder(order.id);
          break;
        case "deliver":
          response = await deliverOrder(order.id);
          break;
        default:
          return;
      }

      // Update local order status
      if (onStatusUpdate) {
        onStatusUpdate(order.id, { status: response.status || action + "ed" });
      }

      // Reload order details
      await loadOrderDetails();
    } catch (err) {
      setError(err.message || `Failed to ${action} order`);
      console.error(`Error ${action}ing order:`, err);
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveTable = async () => {
    if (!order?.id || !String(tableName).trim()) return;

    setError(null);
    try {
      const id = extractOrderId(order.id);
      if (!id) throw new Error("Invalid order ID");

      await http.post(`/fe/orders/${id}/table`, {
        table_num: String(tableName).trim(),
      });

      await loadOrderDetails();
    } catch (err) {
      setError(err.message || "Failed to save table assignment");
      console.error("Error saving table:", err);
    }
  };

  // Helper function to extract numeric ID (same as in orders.service.js)
  const extractOrderId = (orderId) => {
    if (!orderId) return null;
    const str = String(orderId);
    // If it's already a number, return it
    if (/^\d+$/.test(str)) return str;
    // If it has "ORD" prefix, extract the number
    const match = str.match(/(\d+)$/);
    return match ? match[1] : str;
  };

  if (!isOpen) return null;

  const displayOrder = orderDetails || order;
  const canAccept = displayOrder?.status === "pending";
  const canDecline = displayOrder?.status === "pending";
  const canDeliver =
    displayOrder?.status === "accepted" || displayOrder?.status === "inProcess";

  return (
    <div className="lo-modal-overlay" onClick={onClose}>
      <div className="lo-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="lo-modal-header">
          {/* <h2>Order Details #{getOrderIdString(displayOrder?.id || order?.id)}</h2> */}
          <h2>
            Order Details #{getOrderIdString(displayOrder?.id || order?.id)}
          </h2>
          <button
            className="lo-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <LuX size={24} />
          </button>
        </div>

        {loading && !orderDetails ? (
          <div className="lo-modal-loading">Loading order details...</div>
        ) : error && !orderDetails ? (
          <div className="lo-modal-error">
            <p>{error}</p>
            <button
              onClick={loadOrderDetails}
              className="lo-btn lo-btn--secondary"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="lo-modal-body">
              {error && <div className="lo-modal-error-inline">{error}</div>}

              <div className="lo-detail-section">
                <h3>Customer Information</h3>
                <div className="lo-cards">
                  {/* Name Card */}
                  <div className="lo-info-card">
                    <span className="lo-card-label">Name</span>
                    <span className="lo-card-value">
                      {displayOrder?.customer?.name
                        ? String(displayOrder.customer.name)
                        : displayOrder?.customer
                        ? typeof displayOrder.customer === "object"
                          ? displayOrder.customer.name ||
                            JSON.stringify(displayOrder.customer)
                          : String(displayOrder.customer)
                        : "N/A"}
                    </span>
                  </div>

                  {/* Phone Card */}
                  {displayOrder?.customer?.id && (
                    <div className="lo-info-card">
                      <span className="lo-card-label">Phone</span>
                      <span className="lo-card-value">
                        {displayOrder.customer.id}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="lo-detail-section">
                <h3>Order Information</h3>
                <div className="lo-detail-grid">
                  <div className="lo-detail-item">
                    <span className="lo-detail-label">Order ID:</span>
                    <span className="lo-detail-value">
                      {displayOrder?.orderId
                        ? getOrderIdString(displayOrder.orderId)
                        : "N/A"}
                    </span>
                  </div>
                  <div className="lo-detail-item">
                    <span className="lo-detail-label">Status:</span>
                    <span
                      className={`lo-status-badge lo-status-badge--${
                        displayOrder?.status || "pending"
                      }`}
                    >
                      {displayOrder?.status || "pending"}
                    </span>
                  </div>
                  <div className="lo-detail-item">
                    <span className="lo-detail-label">Date:</span>
                    <span className="lo-detail-value">
                      {displayOrder?.placedAt || displayOrder?.date
                        ? new Date(
                            displayOrder.placedAt || displayOrder.date
                          ).toLocaleString()
                        : "N/A"}
                    </span>
                  </div>
                  {displayOrder?.orderType && (
                    <div className="lo-detail-item">
                      <span className="lo-detail-label">Order Type:</span>
                      <span className="lo-detail-value">
                        {displayOrder.orderType}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Table Assignment Section for Dine-in Orders */}
              {(displayOrder?.orderType === "dine-in" ||
                displayOrder?.order_type === "dine-in") && (
                <div className="lo-detail-item">
                  <span className="lo-detail-label">Table Number:</span>

                  {!isEditingTable ? (
                    <span
                      className="lo-detail-value lo-clickable"
                      onClick={() => {
                        setTableName(
                          normalizeTableName(displayOrder?.table_number)
                        );
                        setIsEditingTable(true);
                      }}
                    >
                      {displayOrder?.table_number
                        ? normalizeTableName(displayOrder.table_number)
                        : "Assign table"}
                    </span>
                  ) : (
                    <input
                      type="text"
                      className="lo-table-input inline"
                      value={tableName}
                      autoFocus
                      onChange={(e) => setTableName(e.target.value)}
                      onBlur={async () => {
                        if (String(tableName).trim()) {
                          await handleSaveTable();
                        }
                        setIsEditingTable(false);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === "Enter") {
                          await handleSaveTable();
                          setIsEditingTable(false);
                        }
                        if (e.key === "Escape") {
                          setIsEditingTable(false);
                        }
                      }}
                      placeholder="Table no"
                    />
                  )}
                </div>
              )}

              <div className="lo-detail-section">
                <h3>Order Items ({displayOrder?.items?.length || 0})</h3>

                <div className="lo-items-cards">
                  {(displayOrder?.items || []).map((item, idx) => {
                    const qty = item.qty ?? item.quantity ?? 1;
                    const price = item.unitPrice * item.qty ?? 0;

                    return (
                      <div key={idx} className="lo-item-card">
                        {/* Left index badge */}
                        <div className="lo-item-index">{idx + 1}</div>

                        {/* Item content */}
                        <div className="lo-item-content">
                          <div className="lo-item-title">
                            {item.name || item.item_name}
                          </div>

                          <div className="lo-item-qty-pill">Qty: {qty}</div>

                          {item.description && (
                            <div className="lo-item-desc">
                              {item.description}
                            </div>
                          )}
                        </div>

                        {/* Price */}
                        <div className="lo-item-price">
                          ₹
                          {Number(price).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="cooking-instructions">
                <span style={{ fontWeight: "bold", marginBottom: "1rem" }}>
                  Cooking Instructions :
                </span>

                <span className="value">{displayOrder?.notes || "None"}</span>
              </div>

              <div className="lo-detail-section">
                <div className="lo-total-section">
                  <div className="lo-total-row">
                    <span className="lo-total-label">Subtotal:</span>
                    <span className="lo-total-value">
                      ₹
                      {(displayOrder?.subtotal || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  {displayOrder?.tax !== undefined && displayOrder?.tax !== null && (
                    <div className="lo-total-row">
                      <span className="lo-total-label">Tax:</span>
                      <span className="lo-total-value">
                        ₹
                        {displayOrder.tax.toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="lo-total-row">
                    <span className="lo-total-label">Packing Charge:</span>
                    <span className="lo-total-value">
                      ₹
                      {(displayOrder?.packingCharge || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="lo-total-row">
                    <span className="lo-total-label">Discount:</span>
                    <span className="lo-total-value">
                      ₹
                      {(displayOrder?.discount || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="lo-total-row lo-total-row--final">
                    <span className="lo-total-label">Total:</span>
                    <span className="lo-total-value">
                      ₹
                      {(
                        displayOrder?.total ||
                        displayOrder?.amount ||
                        0
                      ).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="lo-modal-footer">
              {canAccept && (
                <button
                  className="lo-btn lo-btn--accept"
                  onClick={() => handleStatusUpdate("accept")}
                  disabled={updating}
                >
                  <LuCheck size={18} />
                  Accept Order
                </button>
              )}
              {canDecline && (
                <button
                  className="lo-btn lo-btn--decline"
                  onClick={() => handleStatusUpdate("decline")}
                  disabled={updating}
                >
                  <LuX size={18} />
                  Decline Order
                </button>
              )}
              {canDeliver && (
                <button
                  className="lo-btn lo-btn--deliver"
                  onClick={() => handleStatusUpdate("deliver")}
                  disabled={updating}
                >
                  <LuTruck size={18} />
                  Mark as Delivered
                </button>
              )}
              <button
                className="lo-btn lo-btn--secondary"
                onClick={onClose}
                disabled={updating}
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
